import {Button, Typography, Box, useTheme} from '@mui/material';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import {type ChangeEvent, useState} from 'react';
import {EMPTY_ID, useGetTenant, useGetMe} from 'hooks/swr';
import {useTranslation} from 'react-i18next';

export const UploadDialogue = ({
  open,
  onClose,
  title,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
}) => {
  const theme = useTheme();
  const [confirmation, setConfirmation] = useState<string>();
  const {data: me} = useGetMe({resourceId: EMPTY_ID});
  const {data: myCompany} = useGetTenant({resourceId: me?.tenantId});
  const {t} = useTranslation('common');

  const companyName = myCompany?.name;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmation(event.target.value);
  };

  const handleClose = () => {
    setConfirmation('');
    onClose();
  };

  const helperText = (
    <Typography component="div" variant="caption">
      {t('uploadDialogueEnter')}{' '}
      <Box fontWeight="fontWeightBold" display="inline">
        {companyName}
      </Box>{' '}
      {t('uploadDialogueToConfirm')}
    </Typography>
  );

  return (
    <Dialog open={open} fullWidth={false} maxWidth="xs" onClose={onClose} PaperProps={{elevation: 1}}>
      <DialogTitle sx={{backgroundColor: theme.palette.error.main, color: theme.palette.error.contrastText}}>
        {title}
      </DialogTitle>
      <DialogContent
        sx={{
          marginTop: 2,
        }}
      >
        <DialogContentText>{t('uploadDialogueWarningText')}</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          variant="outlined"
          helperText={helperText}
          value={confirmation}
          onChange={handleChange}
          data-testid="upload-confirm-dialog-text-field"
          sx={{
            marginTop: 2,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button
          disabled={companyName !== confirmation}
          onClick={() => {
            onConfirm();
            setConfirmation(undefined);
          }}
          variant="contained"
          color="error"
          data-testid="upload-confirm-dialog-confirm-button"
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
